import { useEffect, useState, type ReactNode } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import { formatLabel } from "../labels";
import type {
  MarketplaceHealthSeverity,
  MarketplaceSignalType,
  MarketplaceTargetType,
  Priority,
} from "../types";

const SIGNAL_TYPES: MarketplaceSignalType[] = [
  "supply_gap",
  "demand_gap",
  "utilization",
  "reliability",
  "conversion",
  "growth_bottleneck",
  "geographic_expansion",
  "future_governed_type",
];

const SEVERITIES: MarketplaceHealthSeverity[] = [
  "low",
  "normal",
  "high",
  "urgent",
  "critical",
];

const PRIORITIES: Priority[] = [
  "low",
  "normal",
  "high",
  "urgent",
  "critical",
];

const TARGET_TYPES: MarketplaceTargetType[] = [
  "market",
  "sport",
  "marketplace_category",
  "business_type",
  "business",
  "bookable_resource",
  "community",
  "creator_program",
];

export function ConfirmActionDialog({
  open,
  title,
  notice,
  actions,
  requireReason = true,
  requireApproval = false,
  approvalLabel = "Approval ID (when required)",
  extraFields,
  confirmVariant = "primary",
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  notice?: string;
  actions: string[];
  requireReason?: boolean;
  requireApproval?: boolean;
  approvalLabel?: string;
  extraFields?: ReactNode;
  confirmVariant?: "primary" | "danger";
  onClose: () => void;
  onSubmit: (input: {
    action: string;
    reason?: string;
    approval_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const first = actions[0] ?? "";
  const [action, setAction] = useState(first);
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");

  useEffect(() => {
    if (!open) return;
    setAction(first);
    setReason("");
    setApprovalId("");
  }, [open, first]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            loading={busy}
            disabled={
              !action ||
              (requireReason && reason.trim().length < 3) ||
              (requireApproval && approvalId.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                ...(approvalId.trim()
                  ? { approval_id: approvalId.trim() }
                  : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value)}
          options={actions.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {extraFields}
        <Textarea
          label={requireReason ? "Reason / rationale" : "Reason (optional)"}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <ApprovalPickerField
          label={approvalLabel}
          value={approvalId}
          onChange={setApprovalId}
          required={requireApproval}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function DetectSignalDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [signalType, setSignalType] =
    useState<MarketplaceSignalType>("supply_gap");
  const [severity, setSeverity] =
    useState<MarketplaceHealthSeverity>("normal");
  const [targetType, setTargetType] =
    useState<MarketplaceTargetType>("business");
  const [targetId, setTargetId] = useState("");
  const [marketId, setMarketId] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");

  useEffect(() => {
    if (!open) return;
    setSignalType("supply_gap");
    setSeverity("normal");
    setTargetType("business");
    setTargetId("");
    setMarketId("");
    setOwnerUserId("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Detect health signal"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!targetId.trim()}
            onClick={() => {
              const body: Record<string, unknown> = {
                signal_type: signalType,
                severity,
                target_type: targetType,
                target_id: targetId.trim(),
              };
              if (marketId.trim()) body.market_id = marketId.trim();
              if (ownerUserId.trim()) body.owner_user_id = ownerUserId.trim();
              void onSubmit(body);
            }}
          >
            Detect signal
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Creates a detected Marketplace Health Signal. Insight does not mutate
          the source Business, Booking, Community, or Creator Program. No
          invented scoring or auto-remediation.
        </Text>
        <Select
          label="Signal type"
          value={signalType}
          onChange={(event) =>
            setSignalType(event.target.value as MarketplaceSignalType)
          }
          options={SIGNAL_TYPES.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Select
          label="Severity"
          value={severity}
          onChange={(event) =>
            setSeverity(event.target.value as MarketplaceHealthSeverity)
          }
          options={SEVERITIES.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Select
          label="Target type"
          value={targetType}
          onChange={(event) =>
            setTargetType(event.target.value as MarketplaceTargetType)
          }
          options={TARGET_TYPES.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Input
          label="Target ID"
          value={targetId}
          onChange={(event) => setTargetId(event.target.value)}
        />
        <Input
          label="Market ID (optional)"
          value={marketId}
          onChange={(event) => setMarketId(event.target.value)}
        />
        <Input
          label="Owner User ID (optional)"
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Detect failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function IdentifyOpportunityDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [priority, setPriority] = useState<Priority>("normal");
  const [targetType, setTargetType] =
    useState<MarketplaceTargetType>("business");
  const [targetId, setTargetId] = useState("");
  const [marketId, setMarketId] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [impactJson, setImpactJson] = useState("{}");
  const [confidenceJson, setConfidenceJson] = useState("{}");
  const [isMaterial, setIsMaterial] = useState(false);
  const [approvalId, setApprovalId] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPriority("normal");
    setTargetType("business");
    setTargetId("");
    setMarketId("");
    setOwnerUserId("");
    setImpactJson("{}");
    setConfidenceJson("{}");
    setIsMaterial(false);
    setApprovalId("");
    setJsonError(null);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Identify opportunity"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!targetId.trim()}
            onClick={() => {
              let impact: Record<string, unknown> = {};
              let confidence: Record<string, unknown> = {};
              try {
                const impactParsed: unknown = JSON.parse(impactJson || "{}");
                const confidenceParsed: unknown = JSON.parse(
                  confidenceJson || "{}",
                );
                if (
                  typeof impactParsed !== "object" ||
                  impactParsed === null ||
                  Array.isArray(impactParsed) ||
                  typeof confidenceParsed !== "object" ||
                  confidenceParsed === null ||
                  Array.isArray(confidenceParsed)
                ) {
                  setJsonError("Assessments must be JSON objects");
                  return;
                }
                impact = impactParsed as Record<string, unknown>;
                confidence = confidenceParsed as Record<string, unknown>;
                setJsonError(null);
              } catch {
                setJsonError("Invalid JSON for assessments");
                return;
              }
              const body: Record<string, unknown> = {
                priority,
                target_type: targetType,
                target_id: targetId.trim(),
                impact_assessment: impact,
                confidence_assessment: confidence,
                is_material: isMaterial,
              };
              if (marketId.trim()) body.market_id = marketId.trim();
              if (ownerUserId.trim()) body.owner_user_id = ownerUserId.trim();
              if (approvalId.trim()) body.approval_id = approvalId.trim();
              void onSubmit(body);
            }}
          >
            Identify
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Opportunities are independent of Signals (no FK). Material market /
          urgent-critical prioritize paths need Manager or Dual Approval.
          Departments execute under their own modules — this screen does not
          mutate source records.
        </Text>
        <Select
          label="Priority"
          value={priority}
          onChange={(event) => setPriority(event.target.value as Priority)}
          options={PRIORITIES.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Select
          label="Target type"
          value={targetType}
          onChange={(event) =>
            setTargetType(event.target.value as MarketplaceTargetType)
          }
          options={TARGET_TYPES.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Input
          label="Target ID"
          value={targetId}
          onChange={(event) => setTargetId(event.target.value)}
        />
        <Input
          label="Market ID (optional)"
          value={marketId}
          onChange={(event) => setMarketId(event.target.value)}
        />
        <Input
          label="Owner User ID (optional)"
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
        />
        <Textarea
          label="impact_assessment (JSON object)"
          value={impactJson}
          onChange={(event) => setImpactJson(event.target.value)}
        />
        <Textarea
          label="confidence_assessment (JSON object)"
          value={confidenceJson}
          onChange={(event) => setConfidenceJson(event.target.value)}
        />
        <Checkbox
          label="Material (Manager/Dual Approval when elevated)"
          checked={isMaterial}
          onChange={(event) => setIsMaterial(event.target.checked)}
        />
        <ApprovalPickerField
          label="Approved Approval (when required)"
          value={approvalId}
          onChange={setApprovalId}
        />
        {jsonError ? (
          <Alert tone="danger" title="Invalid assessment">
            {jsonError}
          </Alert>
        ) : null}
        {error ? (
          <Alert tone="danger" title="Identify failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function PatchOpportunityDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
  initialImpact,
  initialConfidence,
  initialPriority,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
  initialImpact: Record<string, unknown>;
  initialConfidence: Record<string, unknown>;
  initialPriority: Priority;
}) {
  const [priority, setPriority] = useState<Priority>(initialPriority);
  const [impactJson, setImpactJson] = useState("{}");
  const [confidenceJson, setConfidenceJson] = useState("{}");
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPriority(initialPriority);
    setImpactJson(JSON.stringify(initialImpact ?? {}, null, 2));
    setConfidenceJson(JSON.stringify(initialConfidence ?? {}, null, 2));
    setJsonError(null);
  }, [open, initialPriority, initialImpact, initialConfidence]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Update opportunity assessment"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            onClick={() => {
              try {
                const impactParsed: unknown = JSON.parse(impactJson || "{}");
                const confidenceParsed: unknown = JSON.parse(
                  confidenceJson || "{}",
                );
                if (
                  typeof impactParsed !== "object" ||
                  impactParsed === null ||
                  Array.isArray(impactParsed) ||
                  typeof confidenceParsed !== "object" ||
                  confidenceParsed === null ||
                  Array.isArray(confidenceParsed)
                ) {
                  setJsonError("Assessments must be JSON objects");
                  return;
                }
                setJsonError(null);
                void onSubmit({
                  priority,
                  impact_assessment: impactParsed,
                  confidence_assessment: confidenceParsed,
                });
              } catch {
                setJsonError("Invalid JSON for assessments");
              }
            }}
          >
            Save assessment
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          PATCH updates priority and opaque assessment JSON only. It does not
          invent metrics or alter source marketplace entities.
        </Text>
        <Select
          label="Priority"
          value={priority}
          onChange={(event) => setPriority(event.target.value as Priority)}
          options={PRIORITIES.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Textarea
          label="impact_assessment"
          value={impactJson}
          onChange={(event) => setImpactJson(event.target.value)}
        />
        <Textarea
          label="confidence_assessment"
          value={confidenceJson}
          onChange={(event) => setConfidenceJson(event.target.value)}
        />
        {jsonError ? (
          <Alert tone="danger" title="Invalid assessment">
            {jsonError}
          </Alert>
        ) : null}
        {error ? (
          <Alert tone="danger" title="Update failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}
